export type ReplicationBackoffConfig = {
  baseDelayMs: number;
  maxDelayMs: number;
  jitterRatio: number;
};

export const DEFAULT_REPLICATION_BACKOFF_CONFIG: ReplicationBackoffConfig = {
  baseDelayMs: 3_000,
  maxDelayMs: 5 * 60_000,
  jitterRatio: 0.3,
};

export type ReplicationBackoffGate = {
  waitIfBackingOff: () => Promise<void>;
  onSuccess: () => void;
  onError: () => void;
  reset: () => void;
  getConsecutiveFailures: () => number;
};

export type CreateReplicationBackoffGateOptions = {
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  random?: () => number;
};

export const createReplicationBackoffGate = (
  config: ReplicationBackoffConfig = DEFAULT_REPLICATION_BACKOFF_CONFIG,
  {
    now = Date.now,
    sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    random = Math.random,
  }: CreateReplicationBackoffGateOptions = {},
): ReplicationBackoffGate => {
  let consecutiveFailures = 0;
  let nextAttemptAt = 0;

  return {
    waitIfBackingOff: async () => {
      const delay = nextAttemptAt - now();
      if (delay > 0) await sleep(delay);
    },
    onSuccess: () => {
      consecutiveFailures = 0;
      nextAttemptAt = 0;
    },
    onError: () => {
      consecutiveFailures += 1;
      const exponential = config.baseDelayMs * 2 ** (consecutiveFailures - 1);
      const capped = Math.min(exponential, config.maxDelayMs);
      const jitter = capped * config.jitterRatio * (random() * 2 - 1);
      nextAttemptAt = now() + capped + jitter;
    },
    reset: () => {
      consecutiveFailures = 0;
      nextAttemptAt = 0;
    },
    getConsecutiveFailures: () => consecutiveFailures,
  };
};
